<script setup lang="ts">
import ArrowRight from '@/assets/icon/arrow-right.svg'
import Star from '@/assets/icon/star.svg'
import { LocaleCode } from '@packages/i18n'
import type { CommonTabItem } from '~/components/common/Tabs.vue'
import { formatScoreValue } from '~/utils/formatters'
import type { WebHomeExchangeRankingCategory, WebHomeExchangeRankingLabel } from '~/server/home'

type ExchangeRankingTab = {
  id: string
  label: string
  flag: string
  value: string
}

type ExchangeRankingRow = {
  alias: any
  id: string
  rank: number
  rankBadge?: number
  name: string
  shortName: string
  showName: string
  fullName: string
  status: string
  years: string
  score: string | number
  logo: string
  scoreIcon: string
  detailUrl?: string
  adText: string
  statusLabel?: WebHomeExchangeRankingLabel
  yearsLabel?: WebHomeExchangeRankingLabel
  isAdv?: boolean
  isSupplement?: boolean
  ultimateUltraIco?: string
  labels?: WebHomeExchangeRankingLabel[]
  viewCountUnit?: string
}

const props = defineProps<{
  data?: WebHomeExchangeRankingCategory[]
}>()

const { locale, localePath, t } = useAppI18n()
const COMPACT_TABLE_WIDTH_LOCALES = new Set<LocaleCode>([
  LocaleCode.Zh,
  LocaleCode.ZhCn,
  LocaleCode.ZhHk,
  LocaleCode.ZhTw,
  LocaleCode.En,
  LocaleCode.Ja,
  LocaleCode.Ko,
  LocaleCode.Ar,
])
const AD_CARD_LIMIT = 5
const RANKING_TABLE_LIMIT = 5
const RANK_BADGE_LIMIT = 3

const CategoryDefs = [
  // 顺序即前端一级 tab 顺序；调度只会返回其中有数据的项。
  { code: '82500010', name: '本国排行' },
  { code: '82200010', name: '全球排行' },
  { code: '82100016', name: '品牌排行' },
  { code: '82100019', name: '人气排行' },
  { code: '82200092', name: '资产排行' },
  { code: '82300010', name: '黑平台排行' },
]

// 根据排名返回对应的图标
const getRankIcon = (rank: number): string => {
  const icons: Record<number, string> = {
    1: 'https://resources1.interface003.com/web30/2026/05/21/f3eefdcb-a40c-4f72-bf3c-5af3f89ef1b9.png',
    2: 'https://resources1.interface003.com/web30/2026/05/21/ba4a7977-36d5-400f-a6f7-812f2ac4fe72.png',
    3: 'https://resources1.interface003.com/web30/2026/05/21/e5e9854a-744a-43ea-8893-c9e61bbe2262.png',
  }
  return icons[rank] || ''
}

const getADRankIcon = (rank: number): string => {
  const icons: Record<number, string> = {
    1: 'https://resources1.interface003.com/web30/2026/07/01/356b9063-bfcc-493f-a6db-4d5640cb2aa3.png',
    2: 'https://resources1.interface003.com/web30/2026/07/01/b332add6-b673-45c1-8fa2-0544fe873560.png',
    3: 'https://resources1.interface003.com/web30/2026/07/01/ecd458e5-bb11-4a15-a4fb-08bb5849a6e5.png',
  }
  return icons[rank] || ''
}

const matomo = useMatomo()
const showRankIcon = (rankBadge?: number): rankBadge is number => {
  return typeof rankBadge === 'number' && rankBadge <= RANK_BADGE_LIMIT
}

// 获取监管状态对应的类名，区分合规和其他状态
const getStatusBadgeClass = (status: string): string => {
  const lower = status.toLowerCase()
  if (lower.includes('regulated') || lower.includes('domestique') || lower.includes('compliance')) {
    return 'status-regulated'
  }
  return 'status-default'
}

// 提取和清理年限文本
const formatYearsText = (years: string): string => {
  if (!years) return ''
  return years.trim()
}

const activeTab = ref<string | null>(null)

const exchangeCategories = computed<WebHomeExchangeRankingCategory[]>(() =>
  Array.isArray(props.data) ? props.data : [],
)

const getCategoryId = (category: WebHomeExchangeRankingCategory, index: number) => {
  return category.code ? String(category.code) : `exchange-category-${index}`
}

// const buildRankingPageUrl = (category: WebHomeExchangeRankingCategory, index: number) => {
//   const categoryId = getCategoryId(category, index)
//   return `/${locale}/ranking/${encodeURIComponent(categoryId)}`
// }

const tabs = computed<ExchangeRankingTab[]>(() => {
  return exchangeCategories.value
    .map((category, index) => {
      const id = getCategoryId(category, index)
      const label = category.name || category.code || ''

      if (!label) {
        return null
      }

      return {
        id,
        label,
        flag: id === '82500010' ? category?.flag || '' : '',
        value: id,
      } satisfies ExchangeRankingTab
    })
    .filter((item): item is ExchangeRankingTab => item !== null)
})

const activeCategory = computed(() => {
  return exchangeCategories.value.find((category, index) => getCategoryId(category, index) === activeTab.value)
})

const POPULARITY_TAB_ID = '82100019'
const ASSET_RESERVE_TAB_ID = '82200092'
// 交易量排行榜 ID，与排行榜页 82400010 的指标列保持一致。
const BUSINESS_OPERATION_TAB_ID = '82400010'

const activeMetricColumn = computed(() => {
  if (activeTab.value === BUSINESS_OPERATION_TAB_ID) {
    return {
      label: t('89609'),
    }
  }

  if (activeTab.value === POPULARITY_TAB_ID) {
    return {
      label: t('84423'),
    }
  }

  if (activeTab.value === ASSET_RESERVE_TAB_ID) {
    return {
      label: t('84422'),
    }
  }

  return {
    label: t('82495'),
  }
})

const moreRoute = computed(() => {
  const activeTabName = activeCategory.value?.englishName || activeCategory.value?.name || ''
  return localePath({
    name: 'best-exchanges-slug',
    params: {
      slug: activeTabName ? [activeTabName] : [],
    },
  })
})

// 当前分类的完整榜单数据，供广告卡片和排行榜表格拆分展示
const allRankingRows = computed<ExchangeRankingRow[]>(() => {
  const rows = activeCategory.value?.list

  if (!Array.isArray(rows)) {
    return []
  }

  return rows
    .map((item, index) => {
      const shortName = item.shortName || ''
      const showName = item.showName || ''
      const fullName = item.fullName || ''
      const name = shortName || item.localShortName || showName || fullName

      if (!name) {
        return null
      }

      const rank = index + 1
      const statusLabel = item.labels?.find((label) => label.labelType === 107)
      const yearsLabel = item.labels?.find((label) => label.labelType === 101)
      const fallbackStatusLabel = item.labels?.find((label) => label.labelType !== 101)

      return {
        id: item.traderCode || `exchange-row-${rank}-${name}`,
        rank,
        name,
        shortName,
        showName,
        fullName,
        status: (statusLabel ?? fallbackStatusLabel)?.content ?? '',
        years: yearsLabel?.content ?? '',
        score: item.score || '--',
        logo: item.icoSquare || '',
        scoreIcon: item.scoreIcon || '',
        // detailUrl: buildExchangeDetailUrl(item.traderCode),
        adText: item.slogan,
        statusLabel: statusLabel ?? fallbackStatusLabel,
        yearsLabel,
        isAdv: item.isAdv || false,
        ultimateUltraIco: item.ultimateUltraIco || '',
        labels: item.labels || [],
        alias: item.alias,
        viewCountUnit: item.viewCountUnit || '',
      } as ExchangeRankingRow
    })
    .filter((item): item is ExchangeRankingRow => item !== null)
})

const adRankingRows = computed<ExchangeRankingRow[]>(() => {
  const adRows = allRankingRows.value.filter((item) => item.isAdv === true)

  if (adRows.length >= AD_CARD_LIMIT) {
    return adRows.slice(0, AD_CARD_LIMIT)
  }

  const adRowIds = new Set(adRows.map((item) => item.id))
  const supplementRows = allRankingRows.value
    .filter((item) => !adRowIds.has(item.id))
    .slice(0, AD_CARD_LIMIT - adRows.length)
    .map((item, index) => ({
      ...item,
      rankBadge: index + 1,
      isSupplement: true,
    }))

  return [...adRows, ...supplementRows]
})

const rankingRows = computed<ExchangeRankingRow[]>(() => {
  const adRankingRowIds = new Set(adRankingRows.value.map((item) => item.id))
  const adSupplementCount = adRankingRows.value.filter((item) => item.isSupplement).length

  return allRankingRows.value
    .filter((item) => !adRankingRowIds.has(item.id))
    .slice(0, RANKING_TABLE_LIMIT)
    .map((item, index) => ({
      ...item,
      rankBadge: adSupplementCount + index + 1,
    }))
})

const hasExchangeContent = computed(() => exchangeCategories.value.some((category) => category.list?.length))
const isCompactTableWidthLocale = computed(() => COMPACT_TABLE_WIDTH_LOCALES.has(locale))

const exchangeColumnWidthStyle = computed<Record<string, string>>(() => {
  if (isCompactTableWidthLocale.value) {
    return {
      '--exchange-col-exchange-width': '270px',
      '--exchange-col-status-width': '180px',
      '--exchange-col-time-width': '160px',
      '--exchange-col-score-width': '160px',
    }
  }

  return {
    '--exchange-col-exchange-width': '295px',
    '--exchange-col-status-width': '237px',
    '--exchange-col-time-width': '142px',
    '--exchange-col-score-width': '96px',
  }
})

const goRankingPage = async (url?: string) => {
  if (!url) {
    return
  }

  await navigateTo(url)
}

const getLabelStyle = (label?: WebHomeExchangeRankingLabel): Record<string, string> => {
  if (!label) {
    return {}
  }

  return {
    ...(label.borderColor ? { borderColor: label.borderColor } : {}),
    ...(label.backgroundColor ? { backgroundColor: label.backgroundColor } : {}),
    ...(label.fontColor ? { color: label.fontColor } : {}),
  }
}

watch(
  tabs,
  (nextTabs) => {
    if (nextTabs.length === 0) {
      activeTab.value = null
      return
    }

    const fallbackTab = nextTabs[0]

    if (!nextTabs.some((tab) => tab.id === activeTab.value)) {
      activeTab.value = fallbackTab?.id || null
    }
  },
  { immediate: true },
)

/** Tab 切换后仅记录当前分类，选中状态由 CommonTabs 的 v-model 同步。 */
const onTabChange = (_tab: CommonTabItem, index: number) => {
  const tab = tabs.value[index]

  if (!tab) {
    return
  }

  matomo.trackEvent('home_broker_rank_tab', {
    af_type: CategoryDefs.find((item) => item.code === tab.id)?.name,
  })
}

const homeBrokerRankCard = (item: { name: any; code: any; isAdv: any }) => {
  matomo.trackEvent('home_broker_rank_card', {
    af_name: item.name,
    af_code: item.code,
    af_judge: item.isAdv,
  })
}

const homeBrokerRankClick = (item, href) => {
  matomo.trackEvent('home_broker_rank_click', {
    af_type: CategoryDefs.find((i) => i.code === activeTab.value)?.name,
    af_name: item.name,
    af_code: item.id,
  })
  navigateTo(href, { external: true, open: { target: '_blank' } })
}

const goRanking = () => {
  matomo.trackEvent('home_broker_list', {
    af_source: '首页',
  })
}

/** 按交易所记录榜单名称是否发生文本溢出，避免相邻行共用悬浮状态。 */
const rankingNameOverflowMap = ref<Record<string, boolean>>({})
const isOverflow = ref(false)

/** 检查指定榜单交易所名称是否需要展示完整名称提示。 */
const checkRankingNameOverflow = (event: MouseEvent, exchangeId: string) => {
  const target = event.currentTarget as HTMLElement

  if (target) {
    rankingNameOverflowMap.value[exchangeId] = target.scrollWidth > target.clientWidth
  }
}

const checkOverflow = (event: MouseEvent) => {
  const target = event.currentTarget as HTMLElement
  if (target) {
    isOverflow.value = target.scrollWidth > target.clientWidth
  }
}
</script>

<template>
  <div v-if="hasExchangeContent" class="exchange-container" :style="exchangeColumnWidthStyle">
    <!-- 头部栏 -->
    <div class="exchange-header">
      <h2 class="title">{{ t('82491') }}</h2>
      <NuxtLink :to="moreRoute" class="more-btn" aria-label="View more exchanges" target="_blank" @click="goRanking">
        <ArrowRight class="arrow-icon size-[24px] rtl:rotate-180" />
      </NuxtLink>
    </div>

    <!-- 过滤导航 -->
    <CommonTabs
      v-if="tabs.length > 0"
      v-model="activeTab"
      :tabs="tabs"
      frompage="homepage"
      :mask-width="48"
      aria-label="Exchange rankings"
      class="tabs-nav-container"
      style="--common-tabs-width: 100%; --common-tabs-max-width: 100%; --common-tab-font-weight: 500"
      @change="onTabChange"
    />

    <!-- 推荐的 AD 交易所卡片区 -->
    <div v-if="adRankingRows.length > 0" class="ad-cards-grid">
      <NuxtLink
        v-for="item in adRankingRows"
        :key="item.id"
        :class="['ad-card', { 'ultra-ad-card': item.ultimateUltraIco }]"
        :to="localePath({ name: 'exchange-detail', params: { slug: item.alias } })"
        target="_blank"
        @click="homeBrokerRankCard(item)"
      >
        <CommonImage
          v-if="item.isSupplement && showRankIcon(item.rankBadge)"
          :src="getADRankIcon(item.rankBadge)"
          class="ad-rank-icon"
          alt=""
          loading="eager"
        />
        <span v-else-if="item.isSupplement" class="ad-rank-num">{{ item.rank }}</span>
        <span v-else-if="item.adText" class="ad-tag">{{ item.adText }}</span>

        <div class="logo-box">
          <CommonImage :src="item.logo" :alt="`${item.name} logo`" class="brand-logo" loading="eager" />
          <CommonImage
            v-if="item.ultimateUltraIco"
            :src="item.ultimateUltraIco"
            class="ultra-icon"
            alt=""
            loading="eager"
          />
        </div>

        <el-tooltip
          :content="item.shortName"
          placement="bottom"
          popper-class="exchange-name-tooltip"
          :show-arrow="false"
          :offset="4"
          :disabled="!isOverflow"
        >
          <h3 class="card-name" @mouseenter="checkOverflow">{{ item.shortName }}</h3>
        </el-tooltip>

        <div class="card-meta">
          <el-tooltip
            :content="
              item.labels && item.labels.length > 0
                ? item.labels.map((l) => l.content).join(' | ')
                : item.status || item.years
            "
            placement="bottom"
            popper-class="exchange-name-tooltip"
            :show-arrow="false"
            :offset="4"
            :disabled="!isOverflow"
          >
            <span class="meta-status" @mouseenter="checkOverflow">
              <template v-if="item.labels && item.labels.length > 0">
                <template v-for="(label, index) in item.labels" :key="index">
                  <span :style="{ color: label.fontColor }">{{ label.content }}</span>
                  <span v-if="index < item.labels.length - 1" class="separator mx-[4px] text-[#d8d9db]">|</span>
                </template>
              </template>
              <template v-else>
                {{ item.status || item.years }}
              </template>
            </span>
          </el-tooltip>
        </div>
      </NuxtLink>
    </div>

    <!-- 排行榜表格 -->
    <div v-if="rankingRows.length > 0" class="ranking-table-wrapper">
      <table class="ranking-table">
        <thead>
          <tr class="table-header">
            <th class="col-rank">#</th>
            <th class="col-exchange">{{ t('82491') }}</th>
            <th class="col-status">{{ t('82382') }}</th>
            <th class="col-time">{{ t('80342') }}</th>
            <th class="col-score">{{ activeMetricColumn.label }}</th>
          </tr>
        </thead>
        <tbody>
          <NuxtLink
            v-for="item in rankingRows"
            :key="item.id"
            custom
            :to="localePath({ name: 'exchange-detail', params: { slug: item.alias } })"
            v-slot="{ href }"
          >
            <tr
              :class="[
                'table-row cursor-pointer hover:bg-[rgba(17,45,115,0.04)]',
                { 'ultra-row': item.ultimateUltraIco },
              ]"
              @click="homeBrokerRankClick(item, href)"
            >
              <!-- 排名徽标 -->
              <td class="col-rank h-[64px]">
                <CommonImage
                  v-if="showRankIcon(item.rankBadge)"
                  :src="getRankIcon(item.rankBadge)"
                  class="mx-auto block h-[18px] w-[18px]"
                  alt=""
                  loading="eager"
                />
                <span v-else class="rank-num">
                  {{ item.rank }}
                </span>
              </td>

              <!-- 交易所标志和名字 -->
              <td class="col-exchange">
                <div class="exchange-info">
                  <!-- SVG Logo 标志 -->
                  <CommonImage :src="item.logo" :alt="`${item.name} logo`" class="list-logo-box" loading="eager" />

                  <el-tooltip
                    :content="item.shortName"
                    placement="top"
                    popper-class="exchange-name-tooltip"
                    :show-after="300"
                    :show-arrow="false"
                    :offset="4"
                    :disabled="!rankingNameOverflowMap[item.id]"
                  >
                    <span class="exchange-name" @mouseenter="checkRankingNameOverflow($event, item.id)">
                      {{ item.shortName }}
                    </span>
                  </el-tooltip>
                  <CommonImage
                    v-if="item.ultimateUltraIco"
                    :src="item.ultimateUltraIco"
                    class="ultra-icon"
                    alt=""
                    loading="eager"
                  />
                </div>
              </td>

              <!-- 监管状态 -->
              <td class="col-status">
                <span
                  :class="['status-label', getStatusBadgeClass(item.status)]"
                  :style="getLabelStyle(item.statusLabel)"
                >
                  {{ item.status }}
                </span>
              </td>

              <!-- 运营时间 -->
              <td class="col-time">
                <span class="operating-time-text" :style="getLabelStyle(item.yearsLabel)">
                  {{ formatYearsText(item.years) }}
                </span>
              </td>

              <!-- 评分/人气/资产 -->
              <td class="col-score">
                <div class="score-pill flex items-center gap-[6px]">
                  <!-- <CommonImage
                    v-if="activeTab !== POPULARITY_TAB_ID && activeTab !== ASSET_RESERVE_TAB_ID"
                    src="https://resources1.interface003.com/web30/2026/06/27/c39ab257-18e0-423e-8579-d81791e03605.png"
                    class="h-[14px] w-[14px]"
                    loading="eager"
                  />
                  <CommonImage
                    v-else-if="activeTab === POPULARITY_TAB_ID"
                    src="https://resources1.interface003.com/web30/2026/06/30/9d1d5cc9-1ea5-480d-92a1-d1dc999bb424.png"
                    class="h-[14px] w-[14px]"
                    loading="eager"
                  />
                  <CommonImage
                    v-else-if="activeTab === ASSET_RESERVE_TAB_ID"
                    src="https://resources1.interface003.com/web30/2026/06/30/29183a79-bf19-4f0c-ad55-8ceece9d42d0.png"
                    class="h-[14px] w-[14px]"
                    loading="eager"
                  /> -->
                  <CommonImage v-if="item.scoreIcon" :src="item.scoreIcon" class="h-[14px] w-[14px]" loading="eager" />
                  <span class="score-text"> {{ item.score }}{{ item.viewCountUnit || '' }} </span>
                </div>
              </td>
            </tr>
          </NuxtLink>
        </tbody>
      </table>
    </div>

    <NuxtLink
      v-if="rankingRows.length > 4"
      :to="moreRoute"
      target="_blank"
      class="ranking-more-link flex w-full cursor-pointer items-center justify-center gap-[0px] py-[8px]"
      aria-label="View more exchanges"
      @click="goRanking"
    >
      <span class="text-[14px] leading-[22px] font-[400] text-[#292a2e]">{{ t('84570') }}</span>
      <span class="flex size-[32px] items-center justify-center p-[8px]">
        <ArrowRight class="size-[24px] rtl:rotate-180" />
      </span>
    </NuxtLink>
  </div>
</template>

<style scoped lang="less">
.exchange-container {
  width: 820px;
  // 1. 头部区域
  .exchange-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;

    .title {
      color: #292a2e;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 32px;
    }

    .more-btn {
      background: none;
      border: none;
      color: #9ba0ae;
      cursor: pointer;
      padding: 6px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.25s cubic-bezier(0.25, 1, 0.5, 1);
      .arrow-icon {
        width: 24px;
        height: 24px;
        &:hover {
          :deep(path) {
            fill: #112d73 !important;
          }
        }
      }
    }
  }

  // 2. 过滤导航标签容器
  .tabs-nav-container {
    margin-bottom: 32px;
  }

  // 3. 广告交易所网格卡片
  .ad-cards-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin-bottom: 20px;

    .ad-card {
      position: relative;
      overflow: hidden;
      border-radius: 12px;
      border: 0.5px solid rgba(11, 18, 40, 0.14);
      cursor: pointer;
      padding: 24px 12px;
      background-color: #ffffff;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      transition: box-shadow 0.2s ease;

      &.ultra-ad-card {
        background: linear-gradient(180deg, #ffffff 0%, #f8f5ff 100%);
      }

      &:hover {
        box-shadow: 0 12px 32px #08133e14;
      }

      .ad-tag {
        position: absolute;
        top: 0;
        left: 0;
        height: 26px;
        padding: 8px;
        border-radius: 12px 0;
        background: #f8f8f8;
        color: #7d818a;
        font-size: 12px;
        font-weight: 400;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 12px;
      }

      .ad-rank-icon,
      .ad-rank-num {
        position: absolute;
      }

      .ad-rank-icon {
        top: -2px;
        left: 0;
        width: 24px;
        height: 24px;
      }

      .ad-rank-num {
        display: flex;
        top: 0;
        left: 0;
        color: #8c8f97;
        font-size: 14px;
        font-weight: 400;
        width: 26px;
        height: 26px;
        padding: 10px;
        justify-content: center;
        align-items: center;
        border-radius: 12px 0;
        background-color: #f8f8f8;
      }

      .logo-box {
        position: relative;
        width: 52px;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        margin-bottom: 16px;
        border-radius: 6px;

        .brand-logo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .ultra-icon {
          position: absolute;
          right: 4px;
          bottom: 4px;
          width: 25px;
          height: 15px;
        }
      }

      .card-name {
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 18px;
        font-weight: 600;
      }

      .card-meta {
        margin-top: 3px;
        width: 100%;
        overflow: hidden;
        color: #7d818a;
        text-align: center;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;

        .meta-status {
          display: block;
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  // 4. 排行榜表格设计
  .ranking-table-wrapper {
    background-color: #ffffff;
    .table-header {
    }
    .ranking-table {
      width: 100%;
      table-layout: fixed;
      text-align: start;

      th {
        color: #8c8f97;
        font-size: 13px;
        font-weight: 400;
        padding: 12px;
        border-bottom: 1px solid rgba(30, 32, 34, 0.06);
      }

      // 列宽度控制
      .col-rank {
        text-align: center;
        width: 50px;
        box-sizing: border-box;
      }

      .col-exchange {
        text-align: start;
        width: var(--exchange-col-exchange-width);
        box-sizing: border-box;
      }

      .col-status {
        width: var(--exchange-col-status-width);
        text-align: end;
        box-sizing: border-box;
        font-size: 500;
      }

      .col-time {
        text-align: end;
        width: var(--exchange-col-time-width);
        box-sizing: border-box;
      }

      .col-score {
        text-align: end;
        width: var(--exchange-col-score-width);
      }

      td {
        padding: 12px;
        border-bottom: 1px solid rgba(30, 32, 34, 0.04);
        vertical-align: middle;
      }

      .table-row {
        transition: background-color 0.2s ease;
        cursor: pointer;

        &.ultra-row {
          background: linear-gradient(180deg, #ffffff 0%, #f8f5ff 100%);
        }

        .rank-num {
          color: #8c8f97;
          text-align: center;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
        }

        &:last-child td {
          border-bottom: none;
        }

        &:hover {
          background-color: rgba(17, 45, 115, 0.04);
        }
      }

      // 列表内交易所 Logo + 名称
      .exchange-info {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        min-width: 0;

        .list-logo-box {
          width: 32px;
          height: 32px;
          flex-shrink: 0;
          border-radius: 4px;
          .list-logo {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        .ultra-icon {
          width: 25px;
          height: 15px;
          flex: 0 0 25px;
        }

        .exchange-name {
          display: block;
          min-width: 0;
          max-width: 100%;
          color: #292a2e;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 16px;
          font-weight: 500;
        }
      }

      // 监管状态标签样式
      .status-label {
        color: #292a2e;
        text-align: end;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 24px;
        border: 1px solid transparent;
        border-radius: 4px;
        font-style: normal;
        font-weight: 500;
      }

      // 运营时间样式
      .operating-time-text {
        color: #292a2e;
        text-align: end;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 24px;
        padding: 2px 0;
        border: 1px solid transparent;
        border-radius: 4px;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
      }

      // 评分药丸样式
      .score-pill {
        height: 32px;
        padding: 0 12px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        gap: 4px;
        background: #f8f8f8;
        border-radius: 20px;

        &:hover {
          background-color: #f1f3f5;
          border-color: rgba(17, 45, 115, 0.1);
        }

        .score-icon {
          width: 14px;
          height: 14px;
        }

        .score-text {
          color: #292a2e;
          text-align: center;
          /* Number/M-WikiFX-16-500 */
          font-family: WikiFX-Medium;
          font-size: 16px;
          font-weight: 500;
        }
      }
    }
  }
}

@media screen and (min-width: 800px) and (max-width: 1200px) {
  .exchange-container {
    width: 100%;
    min-width: 0;
    margin-top: 50px;
  }
}
@media screen and (max-width: 799px) {
  .exchange-container {
    width: 800px;
    min-width: 0;
    margin-top: 50px;
    margin-top: 50px;
  }
}
</style>

<style lang="less">
.exchange-name-tooltip.el-popper {
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.2),
    0 8px 20px 0 rgba(0, 0, 0, 0.12) !important;
  font-size: 14px !important;
  color: rgba(0, 0, 0, 0.85) !important;
  font-weight: 400 !important;
  padding: 7px 12px !important;
  background-color: #ffffff !important;
  border: none !important;
  border-radius: 6px !important;
  line-height: 1.5 !important;
}
</style>
